'use client';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { DesktopCarousel } from '../shared/desktop-carousel';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { Heading } from '../shared/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

interface CustomProductsCarouselProps {
  products: ProductDisplayData[];
  locale?: string;
  query?: FetchProductQuery;
  title?: string;
  inForm?: boolean;
  onProductSelect?: (name: string) => void;
  selectedProductName?: string;
  onNextStep?: () => void; // Add new prop for next step
  className?: {
    section?: string;
    container?: string;
  };
}

export function CustomProductsCarousel({
  products,
  locale,
  query,
  title,
  inForm,
  onProductSelect,
  selectedProductName,
  onNextStep,
  className,
}: CustomProductsCarouselProps) {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const { data } = useQuery({
    queryKey: [
      'PERSONALIZED_PRODUCTS_CAROUSEL',
      locale,
      query,
      me?.customer.id ?? null,
      me?.authToken ?? null,
    ],
    queryFn: () => fetchProducts({ locale: locale!, query, authToken: me?.authToken }),
    enabled: isInitialized && Boolean(me?.authToken) && Boolean(locale) && Boolean(query),
  });
  const displayedProducts = data?.data ?? products;

  const nextButton = (
    <button
      type="button"
      disabled={!selectedProductName}
      className={cn(
        'flex h-12 w-full cursor-pointer items-center justify-center bg-primary px-6 text-sm font-medium text-black sm:w-fit sm:text-base lg:h-14',
        selectedProductName ? 'opacity-100' : 'opacity-50',
      )}
      onClick={selectedProductName ? onNextStep : undefined}
    >
      {t('text1027')}
    </button>
  );

  return (
    <>
      <CarouselProvider>
        <section className={cn('custom-section', 'px-0 md:px-6', className?.section)}>
          <div
            className={cn(
              'custom-container',
              'flex flex-col items-start justify-start gap-12 sm:gap-16 lg:gap-20 xl:gap-24',
              className?.container,
            )}
          >
            {title && <Heading title={title} className={{ container: 'px-6 md:px-0' }} />}

            <div className="w-full">
              <DesktopCarousel
                responsive
                className={{
                  container: 'pl-6 md:pl-0',
                  slide: 'w-full max-w-[340px] sm:max-w-[400px] md:max-w-[397px]',
                }}
                slides={displayedProducts.map(product => (
                  <ProductCard
                    product={product}
                    key={product.slug}
                    inCarousel
                    inForm={inForm}
                    onSelect={onProductSelect}
                    isSelected={selectedProductName === product.name}
                  />
                ))}
                inForm={inForm} // Pass inForm to DesktopCarousel
                nextButton={inForm ? nextButton : undefined} // Pass next button conditionally
              />
            </div>
          </div>
        </section>
      </CarouselProvider>
    </>
  );
}
